import { CSSProperties, FC, ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';
import { CloseIcon } from '@/components/base/Icons/RocheIconPack';
import Search from '@/storybook/components/Search/Search';
import { SEARCH_SIZE, SEARCH_VARIANT } from '@/storybook/components/Search/Search.constants';
import Button from '@/storybook/components/Button/Button';
import Text from '@/storybook/components/Text/Text';
import styles from './TableActionBar.module.css';

const DEFAULT_TEST_ID = 'table-action-bar';
const TABLE_ACTION_BAR_SEARCH_DEBOUNCE_MS = 300;

const getWidthStyle = (
  width: CSSProperties['width'] | undefined,
  maxWidth: CSSProperties['maxWidth'] | undefined,
): CSSProperties | undefined => {
  return width !== undefined || maxWidth !== undefined ? { width, maxWidth } : undefined;
};

const getContentSlotStyle = (
  width: CSSProperties['width'] | undefined,
  maxWidth: CSSProperties['maxWidth'] | undefined,
): CSSProperties | undefined => {
  if (width !== undefined) {
    return { width, maxWidth, flex: '0 0 auto' };
  }

  return maxWidth !== undefined ? { maxWidth } : undefined;
};

export interface FilterChipItem {
  id: string;
  label: string;
  icon?: ReactNode;
}

export interface TableActionBarProps {
  'showSearch'?: boolean;
  'searchValue'?: string;
  'searchPlaceholder'?: string;
  'width'?: CSSProperties['width'];
  'maxWidth'?: CSSProperties['maxWidth'];
  'filterSlotWidth'?: CSSProperties['width'];
  'filterSlotMaxWidth'?: CSSProperties['maxWidth'];
  'searchWidth'?: CSSProperties['width'];
  'searchMaxWidth'?: CSSProperties['maxWidth'];
  'onSearchChange'?: (value: string) => void;
  'onSearchClear'?: () => void;
  'filterSlot'?: ReactNode;
  'actionSlot'?: ReactNode;
  'activeFilters'?: ReadonlyArray<FilterChipItem>;
  'onFilterRemove'?: (filterId: string) => void;
  'onFiltersClearAll'?: () => void;
  'showAdjustTable'?: boolean;
  'onAdjustTableClick'?: () => void;
  'className'?: string;
  'additionalActionBarElement'?: ReactNode;
  'data-testid'?: string;
}

interface FilterChipProps {
  chip: FilterChipItem;
  onRemove?: (id: string) => void;
  dataTestId: string;
}

const FilterChip: FC<FilterChipProps> = ({ chip, onRemove, dataTestId }) => {
  const { t } = useTranslation();

  const handleRemove = () => {
    onRemove?.(chip.id);
  };

  return (
    <Text as="span" className={styles.filterChip} data-testid={`${dataTestId}-chip-${chip.id}`}>
      {chip.icon && (
        <span className={styles.filterChipIcon} aria-hidden="true">
          {chip.icon}
        </span>
      )}
      <span className={styles.filterChipLabel}>{chip.label}</span>
      {onRemove && (
        <button
          type="button"
          className={styles.filterChipRemoveButton}
          onClick={handleRemove}
          aria-label={t('global.table.removeSecondaryFilter', { label: chip.label })}
          data-testid={`${dataTestId}-chip-remove-${chip.id}`}
        >
          <CloseIcon aria-hidden="true" className={styles.filterChipRemoveIcon} />
        </button>
      )}
    </Text>
  );
};

const TableActionBar: FC<TableActionBarProps> = ({
  showSearch = false,
  searchValue = '',
  searchPlaceholder,
  width,
  maxWidth,
  filterSlotWidth,
  filterSlotMaxWidth,
  searchWidth,
  searchMaxWidth,
  onSearchChange,
  onSearchClear,
  filterSlot,
  actionSlot,
  activeFilters = [],
  onFilterRemove,
  onFiltersClearAll,
  showAdjustTable = false,
  onAdjustTableClick,
  className,
  additionalActionBarElement,
  'data-testid': dataTestId = DEFAULT_TEST_ID,
}) => {
  const { t } = useTranslation();

  const hasActiveFilters = activeFilters.length > 0;
  const hasActionSlot = !!actionSlot;
  const hasMainRowContent =
    !!filterSlot || showSearch || showAdjustTable || hasActionSlot || additionalActionBarElement;
  const hasStretchContent = !!filterSlot || showSearch;
  const isSearchOnlyLayout = showSearch && !filterSlot;
  const resolvedSearchPlaceholder = searchPlaceholder ?? t('global.table.searchPlaceholder');
  const actionBarStyle = useMemo(() => getWidthStyle(width, maxWidth), [width, maxWidth]);
  const filterSlotStyle = useMemo(
    () => getContentSlotStyle(filterSlotWidth, filterSlotMaxWidth),
    [filterSlotWidth, filterSlotMaxWidth],
  );
  const searchWrapperStyle = useMemo(
    () => getContentSlotStyle(searchWidth, searchMaxWidth),
    [searchWidth, searchMaxWidth],
  );

  if (!hasActiveFilters && !hasMainRowContent) {
    return null;
  }

  return (
    <div className={cx(styles.actionBar, className)} style={actionBarStyle} data-testid={dataTestId}>
      {hasActiveFilters && (
        <div className={styles.secondaryFiltersRow} data-testid={`${dataTestId}-secondary-filters`}>
          <Text as="span" className={styles.secondaryFiltersLabel}>
            {t('global.table.secondaryFilters')}
          </Text>
          {activeFilters.map(chip => (
            <FilterChip key={chip.id} chip={chip} onRemove={onFilterRemove} dataTestId={dataTestId} />
          ))}
          {onFiltersClearAll && (
            <Button variant="text" size="S" onClick={onFiltersClearAll} data-testid={`${dataTestId}-clear-all`}>
              {t('global.filters.clearAll')}
            </Button>
          )}
        </div>
      )}
      {hasMainRowContent && (
        <div className={styles.mainRow} data-testid={`${dataTestId}-main-row`}>
          {hasStretchContent && (
            <div
              className={cx(styles.contentGroup, {
                [styles.contentGroupSearchOnly]: isSearchOnlyLayout,
              })}
            >
              {filterSlot && (
                <div
                  className={styles.filterSlot}
                  style={filterSlotStyle}
                  data-testid={`${dataTestId}-filter-slot-wrapper`}
                >
                  {filterSlot}
                </div>
              )}
              {showSearch && (
                <div className={styles.searchWrapper} style={searchWrapperStyle}>
                  <Search
                    size={SEARCH_SIZE.M}
                    variant={SEARCH_VARIANT.BASIC}
                    debounce={TABLE_ACTION_BAR_SEARCH_DEBOUNCE_MS}
                    value={searchValue}
                    placeholder={resolvedSearchPlaceholder}
                    onChange={onSearchChange}
                    onClear={onSearchClear}
                    data-testid={`${dataTestId}-search`}
                  />
                </div>
              )}
            </div>
          )}
          {(hasActionSlot || showAdjustTable) && (
            <div className={styles.trailingActions}>
              {actionSlot}
              {showAdjustTable && (
                <div className={styles.adjustTableWrapper}>
                  <Button
                    variant="text"
                    size="M"
                    onClick={onAdjustTableClick}
                    data-testid={`${dataTestId}-adjust-table`}
                  >
                    {t('global.table.manageColumns')}
                  </Button>
                </div>
              )}
            </div>
          )}
          {additionalActionBarElement && <div>{additionalActionBarElement}</div>}
        </div>
      )}
    </div>
  );
};

export default TableActionBar;
